/**
 * @fileoverview Google Sign-In Button Component
 * @module GoogleSignInButton
 * 
 * Business Context:
 * - Provides one-click Google authentication for Texas homeowners
 * - Simplifies the sign-up/sign-in process
 * - Integrates with existing authentication flow
 * 
 * Technical Context:
 * - Initiates OAuth flow by redirecting to backend Google auth endpoint
 * - Shows loading state while checking OAuth availability
 * - Handles disabled state when OAuth is not configured
 */

import React, { useState, useEffect } from 'react';
import { API_CONFIG } from '../../api/config';
import { clearPostOAuthRedirect, setPostOAuthRedirect } from '../../utils/oauth-redirect';

interface GoogleSignInButtonProps {
  className?: string;
  disabled?: boolean;
  label?: string;
  returnPath?: string;
}

/**
 * Google Sign-In Button
 * Redirects user to backend OAuth endpoint to initiate Google authentication
 */
export const GoogleSignInButton: React.FC<GoogleSignInButtonProps> = ({
  className = '',
  disabled = false,
  label = 'Continue with Google',
  returnPath,
}) => {
  const [isLoading, setIsLoading] = useState(false);
  const [isAvailable, setIsAvailable] = useState<boolean | null>(null);

  // Check if Google OAuth is available on mount
  useEffect(() => {
    const checkOAuthAvailability = async () => {
      try {
        const response = await fetch(`${API_CONFIG.baseURL}/auth/google/status`);
        const data = await response.json();
        setIsAvailable(data.data?.available ?? false);
      } catch (error) {
        console.error('Failed to check Google OAuth availability:', error);
        setIsAvailable(false);
      }
    };

    checkOAuthAvailability();
  }, []);

  const handleGoogleSignIn = () => {
    if (disabled || !isAvailable || isLoading) return;
    
    setIsLoading(true);

    if (returnPath) {
      setPostOAuthRedirect(returnPath);
    } else {
      clearPostOAuthRedirect();
    }
    
    // Redirect to the backend Google OAuth endpoint
    // The backend will handle the OAuth flow and redirect back to frontend
    window.location.href = `${API_CONFIG.baseURL}/auth/google`;
  };

  // Loading state while checking availability
  if (isAvailable === null) {
    return (
      <button
        type="button"
        disabled
        className={`
          w-full flex items-center justify-center gap-3 px-4 py-2.5
          border border-gray-200 rounded-lg bg-gray-50
          text-gray-400 text-sm font-medium cursor-not-allowed
          ${className}
        `}
      >
        <div className="w-4 h-4 rounded-full border-2 border-gray-300 border-t-transparent animate-spin" />
        Loading...
      </button>
    );
  }

  // Show button but disabled if OAuth is not available
  const isButtonDisabled = disabled || isLoading || !isAvailable;

  return (
    <button
      type="button"
      onClick={handleGoogleSignIn}
      disabled={isButtonDisabled}
      className={`
        w-full flex items-center justify-center gap-3 px-4 py-2.5
        border border-gray-200 rounded-lg
        bg-white text-sm font-medium
        transition-all duration-200 
        ${isButtonDisabled 
          ? 'cursor-not-allowed text-gray-400 bg-gray-50' 
          : 'hover:bg-gray-50 text-gray-700 hover:shadow-sm active:scale-[0.99]'}
        ${className}
      `}
    >
      {isLoading ? (
        <>
          <div className="w-4 h-4 rounded-full border-2 border-gray-400 border-t-transparent animate-spin" />
          Redirecting...
        </>
      ) : (
        <>
          {/* Google Logo SVG */}
          <svg className="w-4 h-4" viewBox="0 0 24 24" xmlns="http://www.w3.org/2000/svg">
            <path
              fill="#4285F4"
              d="M22.56 12.25c0-.78-.07-1.53-.2-2.25H12v4.26h5.92c-.26 1.37-1.04 2.53-2.21 3.31v2.77h3.57c2.08-1.92 3.28-4.74 3.28-8.09z"
            />
            <path
              fill="#34A853"
              d="M12 23c2.97 0 5.46-.98 7.28-2.66l-3.57-2.77c-.98.66-2.23 1.06-3.71 1.06-2.86 0-5.29-1.93-6.16-4.53H2.18v2.84C3.99 20.53 7.7 23 12 23z"
            />
            <path
              fill="#FBBC05"
              d="M5.84 14.09c-.22-.66-.35-1.36-.35-2.09s.13-1.43.35-2.09V7.07H2.18C1.43 8.55 1 10.22 1 12s.43 3.45 1.18 4.93l2.85-2.22.81-.62z"
            />
            <path
              fill="#EA4335"
              d="M12 5.38c1.62 0 3.06.56 4.21 1.64l3.15-3.15C17.45 2.09 14.97 1 12 1 7.7 1 3.99 3.47 2.18 7.07l3.66 2.84c.87-2.6 3.3-4.53 6.16-4.53z"
            />
          </svg>
          {label}
        </>
      )}
    </button>
  );
};

